import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import DateFieldInput from "../DateFieldInput";
import { formatCameraAssegnata } from "../../utils/formatCamera";
import {
  resolveStatoOspite,
  ospiteStatoLabel,
  formatDataItShort,
  STATO_PRE_INGRESSO,
  STATO_DIMESSO,
} from "../../utils/ospiteStato";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function todayIso() {
  return new Date().toISOString().split("T")[0];
}

function isPastOrToday(isoDate) {
  if (!isoDate) return false;
  const d = new Date(`${isoDate.slice(0, 10)}T12:00:00`);
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return d.getTime() <= today.getTime();
}

export default function DimissioniRapideDialog({ open, onOpenChange, onSuccess }) {
  const { token } = useAuth();
  const [ospiti, setOspiti] = useState([]);
  const [loadingOspiti, setLoadingOspiti] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fascicoloId, setFascicoloId] = useState("");
  const [dataUscita, setDataUscita] = useState(todayIso());
  const [liberaPosto, setLiberaPosto] = useState(true);

  useEffect(() => {
    if (!open || !token) return;
    setLoadingOspiti(true);
    axios
      .get(`${API_URL}/api/fascicoli-assistenziali`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setOspiti(res.data || []))
      .catch(() => toast.error("Errore nel caricamento degli ospiti"))
      .finally(() => setLoadingOspiti(false));
  }, [open, token]);

  const ospitiSelezionabili = useMemo(
    () =>
      ospiti.filter((o) => {
        if (o.archiviata) return false;
        const stato = resolveStatoOspite(o);
        return stato !== STATO_PRE_INGRESSO;
      }),
    [ospiti]
  );

  const ospiteSelezionato = ospitiSelezionabili.find((o) => o.id === fascicoloId);
  const statoSelezionato = ospiteSelezionato ? resolveStatoOspite(ospiteSelezionato) : null;
  const haCamera = Boolean(ospiteSelezionato?.room_id || ospiteSelezionato?.camera);
  const dimissioneImmediata = isPastOrToday(dataUscita);

  const reset = () => {
    setFascicoloId("");
    setDataUscita(todayIso());
    setLiberaPosto(true);
  };

  const handleClose = (value) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const handleSave = async () => {
    if (!fascicoloId) {
      toast.error("Seleziona un ospite");
      return;
    }
    if (!dataUscita) {
      toast.error("Inserisci la data di uscita");
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/dimissione`,
        {
          data_uscita: dataUscita,
          libera_posto: liberaPosto,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const nome = ospiteSelezionato?.nome_completo || "Ospite";
      const statoLabel = ospiteStatoLabel(data?.stato_ospite);
      toast.success(`Dimissione impostata per ${nome} (${statoLabel})`);
      onSuccess?.(data);
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'impostazione della dimissione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="dialog-dimissioni-rapide">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut className="w-5 h-5 text-orange-600" />
            Imposta dimissione
          </DialogTitle>
          <DialogDescription>
            Registra la data di uscita dell&apos;ospite. Se la data è oggi o nel passato, il posto
            letto può essere liberato automaticamente.
          </DialogDescription>
        </DialogHeader>

        {loadingOspiti ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Caricamento ospiti…
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Ospite *</Label>
              <Select value={fascicoloId} onValueChange={setFascicoloId}>
                <SelectTrigger data-testid="select-ospite-dimissione">
                  <SelectValue placeholder="Seleziona ospite..." />
                </SelectTrigger>
                <SelectContent>
                  {ospitiSelezionabili.length === 0 ? (
                    <SelectItem value="__none" disabled>
                      Nessun ospite disponibile
                    </SelectItem>
                  ) : (
                    ospitiSelezionabili.map((o) => {
                      const stato = resolveStatoOspite(o);
                      const cam =
                        formatCameraAssegnata(o.camera) || o.camera || "Senza camera";
                      const uscita =
                        o.data_uscita && stato !== STATO_DIMESSO
                          ? ` · uscita ${formatDataItShort(o.data_uscita)}`
                          : "";
                      return (
                        <SelectItem key={o.id} value={o.id}>
                          {o.nome_completo} ({cam}) — {ospiteStatoLabel(stato)}
                          {uscita}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data di uscita *</Label>
              <DateFieldInput
                value={dataUscita}
                onChange={setDataUscita}
                data-testid="input-data-uscita-dimissione"
              />
              <p className="text-xs text-slate-500">
                {dimissioneImmediata
                  ? "La data selezionata comporta dimissione effettiva."
                  : "La data selezionata programma la dimissione futura."}
              </p>
            </div>

            {ospiteSelezionato?.data_uscita ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Uscita attuale:{" "}
                <span className="font-medium text-slate-800">
                  {formatDataItShort(ospiteSelezionato.data_uscita)}
                </span>
                {statoSelezionato ? ` (${ospiteStatoLabel(statoSelezionato)})` : ""}
              </div>
            ) : null}

            {dimissioneImmediata && haCamera ? (
              <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="libera-posto-dimissione"
                    checked={liberaPosto}
                    onCheckedChange={(v) => setLiberaPosto(v === true)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="libera-posto-dimissione" className="cursor-pointer text-sm">
                    <span className="font-medium text-slate-800">Libera il posto letto</span>
                    <span className="mt-1 block text-xs text-slate-600 leading-5">
                      Rimuove l&apos;assegnazione camera (
                      {formatCameraAssegnata(ospiteSelezionato?.camera) ||
                        ospiteSelezionato?.camera}
                      ) e rende disponibile il posto.
                    </span>
                  </Label>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={saving}>
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loadingOspiti || ospitiSelezionabili.length === 0}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="btn-conferma-dimissione"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Conferma dimissione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
